/* LIVE ORDER QUOTE — the all-up price before the load goes out, so the dispatcher can
   answer "what'll that cost me?" on the phone. Same rules as the invoice engine. */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Receipt, Copy, MessageSquare, Loader2, Pencil } from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function RateEditor({ rate, onCommit }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(String(rate ?? ""));
  useEffect(() => { setVal(String(rate ?? "")); }, [rate]);
  const commit = () => {
    setEditing(false);
    const n = parseFloat(val);
    if (val.trim() === "" || isNaN(n) || n < 0) onCommit(null);
    else onCommit(Number(n.toFixed(2)));
  };
  if (editing) {
    return (
      <span className="inline-flex items-center gap-0.5">
        <span className="text-muted-industrial">$</span>
        <input autoFocus type="number" step="0.01" min="0" value={val}
          onChange={(e) => setVal(e.target.value)} onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } if (e.key === "Escape") { setEditing(false); setVal(String(rate ?? "")); } }}
          data-testid="quote-rate-input"
          className="w-16 h-5 bg-surface border border-primary-orange px-1 mono text-[10px] text-right rounded-none focus:outline-none" />
      </span>
    );
  }
  return (
    <button type="button" onClick={() => setEditing(true)} data-testid="quote-rate-edit" title="Edit the $/m³ for this order"
      className="inline-flex items-center gap-1 hover:text-primary-orange">
      {money(rate)}<Pencil className="w-2.5 h-2.5 opacity-60" />
    </button>
  );
}

export const OrderQuote = ({ form, customerName, sitePhone, plantId, onRate }) => {
  const [q, setQ] = useState(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  const lines = (form.lines || []).filter((l) => l.mix_id && Number(l.quantity_m3) > 0);
  const body = {
    customer_id: form.customer_id || null,
    lines: lines.map((l) => ({ mix_id: l.mix_id, quantity_m3: Number(l.quantity_m3), price_per_m3: l.price_per_m3 ?? null })),
    mix_id: form.mix_id || null,
    quantity_m3: Number(form.quantity_m3) || null,
    price_per_m3: form.price_per_m3 ?? null,
    extra_tag_ids: form.extra_tag_ids || [],
    surcharge_ids: form.surcharge_ids || [],
    mini_load_surcharge: !!form.mini_load_surcharge,
    site_id: form.site_id || null,
    delivery_km: form.delivery_km === "" || form.delivery_km == null ? null : Number(form.delivery_km),
    plant_id: plantId || null,
    is_cash_sale: !!form.is_cash_sale,
    ex_plant: !!form.ex_plant,
  };
  const key = JSON.stringify(body);

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setBusy(true);
      try { setQ((await api.post("/jobs/quote", JSON.parse(key))).data); }
      catch { setQ(null); }
      finally { setBusy(false); }
    }, 450);
    return () => clearTimeout(timer.current);
  }, [key]);

  if (!q?.priceable) {
    return (
      <div className="border border-industrial p-3 mono text-[11px] uppercase tracking-widest text-muted-industrial" data-testid="order-quote-empty">
        <Receipt className="w-3.5 h-3.5 inline mr-1.5 text-primary-orange" />
        {q?.reason || "Pick a mix and quantity to price this order"}
      </div>
    );
  }

  const asText = [
    `${customerName || q.customer || "Quote"} · ${q.total_m3} m³`,
    ...q.lines.map((l) => `${l.label}: ${money(l.amount)}`),
    `Subtotal (ex GST): ${money(q.subtotal)}`,
    `GST: ${money(q.gst)}`,
    `TOTAL (inc GST): ${money(q.total)}  ·  ${money(q.per_m3_inc_gst)}/m³`,
    ...(q.notes || []),
  ].join("\n");

  const copy = async () => { await navigator.clipboard?.writeText(asText); toast.success("Quote copied"); };

  return (
    <div className="border-2 border-primary-orange" data-testid="order-quote">
      <div className="flex items-center gap-2 px-3 py-2 bg-primary-orange/10 border-b border-primary-orange/40">
        <Receipt className="w-3.5 h-3.5 text-primary-orange" />
        <span className="label-tag text-primary-orange">Price this order</span>
        {q.pricing_class && <span className="mono text-[9px] uppercase tracking-widest text-muted-industrial">class {q.pricing_class}</span>}
        {busy && <Loader2 className="w-3 h-3 animate-spin text-muted-industrial ml-auto" />}
      </div>

      <table className="w-full text-[12px] tabular-nums">
        <tbody>
          {q.lines.map((l, i) => (
            <tr key={i} className="border-b border-industrial/60" data-testid={`quote-line-${l.kind}`}>
              <td className="px-3 py-1.5">
                <div className="mono uppercase text-[10px] tracking-wide">{l.label}</div>
                {l.qty > 0 && l.unit && (
                  <div className="mono text-[10px] text-muted-industrial flex items-center gap-1">
                    <span>{l.qty}{l.unit} ×</span>
                    {onRate && l.kind === "mix" && l.mix_id
                      ? <RateEditor rate={l.rate} onCommit={(v) => onRate(l.mix_id, v)} />
                      : <span>{money(l.rate)}</span>}
                    {l.custom && <span className="text-primary-orange text-[9px] uppercase tracking-widest" data-testid={`quote-rate-custom-${l.mix_id}`}>custom</span>}
                  </div>
                )}
              </td>
              <td className="px-3 py-1.5 text-right font-bold whitespace-nowrap">{money(l.amount)}</td>
            </tr>
          ))}
          <tr className="border-b border-industrial/60">
            <td className="px-3 py-1.5 mono uppercase text-[10px] tracking-widest text-muted-industrial">Subtotal ex GST</td>
            <td className="px-3 py-1.5 text-right font-bold" data-testid="quote-subtotal">{money(q.subtotal)}</td>
          </tr>
          <tr className="border-b border-industrial/60">
            <td className="px-3 py-1.5 mono uppercase text-[10px] tracking-widest text-muted-industrial">GST</td>
            <td className="px-3 py-1.5 text-right" data-testid="quote-gst">{money(q.gst)}</td>
          </tr>
        </tbody>
      </table>

      <div className="px-3 py-2.5 flex items-end justify-between gap-3 bg-primary-orange/10">
        <div>
          <div className="label-tag text-primary-orange">Total inc GST</div>
          <div className="heading text-3xl font-black leading-none" data-testid="quote-total">{money(q.total)}</div>
          <div className="mono text-[10px] text-muted-industrial mt-1" data-testid="quote-per-m3">
            {money(q.per_m3_inc_gst)}/m³ inc GST · {money(q.per_m3_ex_gst)}/m³ ex
          </div>
        </div>
        <div className="flex gap-1.5">
          <button type="button" onClick={copy} data-testid="quote-copy" title="Copy the quote"
            className="mono text-[10px] uppercase tracking-widest px-2.5 py-2 border border-industrial hover:border-primary-orange hover:text-primary-orange inline-flex items-center gap-1"><Copy className="w-3 h-3" /> Copy</button>
          {sitePhone && (
            <a href={`sms:${sitePhone}?&body=${encodeURIComponent(asText)}`} data-testid="quote-sms" title="Text the quote"
              className="mono text-[10px] uppercase tracking-widest px-2.5 py-2 border border-industrial hover:border-primary-orange hover:text-primary-orange inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Text</a>
          )}
        </div>
      </div>

      {(q.notes || []).map((n) => (
        <div key={n} className="px-3 py-1.5 border-t border-industrial mono text-[10px] uppercase tracking-widest text-muted-industrial" data-testid="quote-note">· {n}</div>
      ))}
    </div>
  );
};
